import React from 'react';
import { Vendor } from '../../types/vendor';
import { TrendingUp, Clock, CheckCircle2, DollarSign, Star, AlertTriangle } from 'lucide-react';

interface Props {
  vendors: Vendor[];
}

function PercentGauge({ value, label, color }: { value: number; label: string; color: string }) {
  const radius = 32;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (value / 100) * circumference;

  return (
    <div className="flex flex-col items-center gap-1">
      <svg width="80" height="80" viewBox="0 0 80 80" className="-rotate-90">
        <circle cx="40" cy="40" r={radius} fill="none" stroke="#1e293b" strokeWidth="7" />
        <circle
          cx="40" cy="40" r={radius} fill="none"
          stroke={color} strokeWidth="7"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
      </svg>
      <span className="text-base font-semibold -mt-10 rotate-0" style={{ color }}>{value.toFixed(0)}%</span>
      <span className="text-[10px] text-slate-400 mt-8 text-center leading-tight max-w-[70px]">{label}</span>
    </div>
  );
}

function MetricCard({ icon, label, value, sub, color }: { icon: React.ReactNode; label: string; value: string; sub?: string; color: string }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <span style={{ color }}>{icon}</span>
        <p className="text-xs text-slate-400">{label}</p>
      </div>
      <p className="text-xl font-semibold" style={{ color }}>{value}</p>
      {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

export const VendorPerformanceDashboard: React.FC<Props> = ({ vendors }) => {
  if (!vendors.length) {
    return (
      <div className="text-center py-10 text-slate-500 text-sm">
        <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-30" />
        No performance data available yet.
      </div>
    );
  }

  const activeVendors = vendors.filter(v => v.status === 'active');

  // Aggregate metrics
  const avgOnTime = activeVendors.reduce((s, v) => s + v.performance.on_time_completion_rate, 0) / (activeVendors.length || 1);
  const avgPassRate = activeVendors.reduce((s, v) => s + v.performance.inspection_pass_rate, 0) / (activeVendors.length || 1);
  const avgResponse = activeVendors.reduce((s, v) => s + v.performance.avg_response_time_hours, 0) / (activeVendors.length || 1);
  const totalJobs = activeVendors.reduce((s, v) => s + v.performance.total_jobs_completed, 0);
  const avgRating = activeVendors.reduce((s, v) => s + v.performance.rating, 0) / (activeVendors.length || 1);

  const topPerformer = [...vendors].sort((a, b) => b.performance.rating - a.performance.rating)[0];
  const bottomPerformer = [...vendors].sort((a, b) => a.performance.rating - b.performance.rating)[0];

  return (
    <div className="space-y-6">
      {/* Aggregate KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard icon={<Clock className="w-4 h-4" />} label="Avg Response Time" value={`${avgResponse.toFixed(1)}h`} sub="Across active vendors" color="#38bdf8" />
        <MetricCard icon={<CheckCircle2 className="w-4 h-4" />} label="Jobs Completed" value={String(totalJobs)} sub="Active vendors only" color="#34d399" />
        <MetricCard icon={<Star className="w-4 h-4" />} label="Avg Rating" value={avgRating.toFixed(1)} sub="Out of 5.0" color="#f59e0b" />
        <MetricCard icon={<TrendingUp className="w-4 h-4" />} label="Active Vendors" value={String(activeVendors.length)} sub={`of ${vendors.length} total`} color="#a78bfa" />
      </div>

      {/* Gauge row */}
      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
        <p className="text-sm font-semibold text-slate-300 mb-4">Fleet Performance Gauges</p>
        <div className="flex justify-around flex-wrap gap-4">
          <PercentGauge value={avgOnTime} label="On-Time Completion" color="#34d399" />
          <PercentGauge value={avgPassRate} label="Inspection Pass Rate" color="#38bdf8" />
          <PercentGauge value={Math.min(100, (totalJobs / Math.max(1, vendors.reduce((s, v) => s + v.performance.total_jobs_assigned, 0))) * 100)} label="Overall Completion" color="#a78bfa" />
        </div>
      </div>

      {/* Per-vendor leaderboard */}
      <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
          <p className="text-sm font-semibold text-slate-300">Vendor Performance Leaderboard</p>
          <span className="text-[10px] text-slate-500">Ranked by rating</span>
        </div>
        <div className="divide-y divide-slate-800">
          {[...vendors]
            .sort((a, b) => b.performance.rating - a.performance.rating)
            .map((vendor, idx) => (
              <div key={vendor.vendor_id} className="flex items-center gap-3 px-4 py-3">
                <span className="text-lg font-semibold w-6 text-center" style={{ color: idx === 0 ? '#f59e0b' : idx === 1 ? '#94a3b8' : idx === 2 ? '#b45309' : '#475569' }}>
                  {idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-200 truncate">{vendor.name}</p>
                  <p className="text-[10px] text-slate-500">{vendor.category_mappings.map(c => c.category_name).join(' Â· ')}</p>
                </div>
                <div className="flex items-center gap-3 text-xs flex-shrink-0">
                  <span className="text-emerald-400">{vendor.performance.on_time_completion_rate.toFixed(0)}%</span>
                  <span className="text-sky-400">{vendor.performance.inspection_pass_rate.toFixed(0)}%</span>
                  <div className="flex items-center gap-0.5">
                    <Star className="w-3 h-3 fill-amber-400 stroke-amber-400" />
                    <span className="text-amber-400 font-semibold">{vendor.performance.rating.toFixed(1)}</span>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Top / Bottom performers */}
      {vendors.length >= 2 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <p className="text-xs font-semibold text-emerald-400">Top Performer</p>
            </div>
            <p className="font-semibold text-slate-100">{topPerformer.name}</p>
            <p className="text-xs text-slate-400 mt-0.5">Rating: {topPerformer.performance.rating.toFixed(1)} Â· On-Time: {topPerformer.performance.on_time_completion_rate.toFixed(0)}%</p>
          </div>
          <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-rose-400" />
              <p className="text-xs font-semibold text-rose-400">Needs Attention</p>
            </div>
            <p className="font-semibold text-slate-100">{bottomPerformer.name}</p>
            <p className="text-xs text-slate-400 mt-0.5">Rating: {bottomPerformer.performance.rating.toFixed(1)} Â· On-Time: {bottomPerformer.performance.on_time_completion_rate.toFixed(0)}%</p>
          </div>
        </div>
      )}
    </div>
  );
};
